import { useEffect, useState } from "react";
import { FormActions } from "../../components/common/FormActions";
import { BooleanTags } from "../../components/common/registerUtils";
import {
  API_SCRUTNIY_WORKFLOWS,
  API_USERS_LIST,
  API_WORKFLOWS,
} from "../../utils/apis";
import {
  createEditAction,
  createViewAction,
  formatDate,
  formatDateTime,
  getForm,
  getRegister,
} from "../../utils/helpers";
import { URL_SCRUTINY_WORKFLOW } from "../../utils/urls";
import useGet from "../common/useGet";

const url = URL_SCRUTINY_WORKFLOW;

// REGISTER
export const useScrutinyWorkflows = () => {
  const tableColumns = () => [
    { id: "workflow_name", header: "Workflow Name", filter: {} },
    { id: "actor", header: "Actor", filter: {} },
    { id: "level_description", header: "Description" },
    { id: "scrutiny_level", header: "Scrutiny Level" },
    { id: "number_of_scrutiny_levels", header: "Number Of Scrutiny Levels" },
    { id: "is_active", header: "Status", filter: {} },
    { id: "created_by", header: "Created By", filter: {} },
    { id: "created_at", header: "Created At", filter: {} },
    { id: "actions", header: "Actions" },
  ];

  const tableData = (data: any) =>
    data?.map((item: any) => ({
      ...item,
      is_active: <BooleanTags status={item?.is_active} />,
      actor: item?.actor_name || item?.actor_username,
      created_at: formatDate(item?.created_at),
      created_by: item?.created_by?.first_name,
      actions: (
        <FormActions
          actions={[
            createEditAction(url, item?.id),
            createViewAction(url, item?.id),
          ]}
        />
      ),
    }));

  const viewData = (item: any) => ({
    sections: [
      {
        title: "Scrutiny Workflows Details",
        fields: {
          workflow: item?.workflow_name,
          actor: item?.actor_name || item?.actor_username,
          level_description: item?.level_description,
          scrutiny_level: item?.scrutiny_level,
          number_of_scrutiny_levels: item?.number_of_scrutiny_levels,
          is_active: <BooleanTags status={item?.is_active} />,
          created_by: item?.created_by?.first_name,
          created_at: formatDateTime(item?.created_at),
        },
      },
    ],
  });

  const formRoute = getForm(url);
  return { tableData, tableColumns, formRoute, viewData };
};

// FORM
export const useScrutinyWorkflowsForm = ({ id, form }) => {
  const [selectedWorkflow, setSelectedWorkflow] = useState<any>(null);
  const [workflowLevelsOptions, setWorkflowLevelsOptions] = useState<any[]>([]);
  const {
    loadData: loadWorkflow,
    loading: loadingWorkflow,
    data: workflowsData,
  } = useGet();
  const { loadData: loadUser, loading: loadingUser, data: userData } = useGet();

  const isEdit = !!id;

  useEffect(() => {
    loadWorkflow({ api: API_WORKFLOWS });
  }, []);

  useEffect(() => {
    loadUser({ api: API_USERS_LIST });
  }, []);

  useEffect(() => {
    if (form?.workflow) {
      const workflow = workflowsData?.results?.find(
        (wf: any) => wf.id === form.workflow,
      );
      setSelectedWorkflow(workflow || null);

      if (workflow?.workflow_levels) {
        const levels = Array.from(
          { length: workflow.workflow_levels },
          (_, i) => ({
            id: i + 1,
            name: `${i + 1}`,
          }),
        );
        setWorkflowLevelsOptions(levels);
      } else {
        setWorkflowLevelsOptions([]);
      }
    }
  }, [form?.workflow, workflowsData]);

  const sections = [
    {
      title: "Scrutiny Workflows Details",
      fields: [
        {
          field_name: "workflow",
          label: "Workflow",
          type: "select",
          props: { required: !isEdit },
          options_data: workflowsData?.results || [],
          loadingWorkflow,
        },
        {
          field_name: "scrutiny_level",
          label: "Workflow Level",
          type: "select",
          props: { required: !isEdit },
          options_data: workflowLevelsOptions,
          loading: !selectedWorkflow && loadingUser,
        },
        {
          field_name: "level_description",
          type: "text",
          props: { required: false },
        },
        {
          field_name: "actor",
          type: "select",
          props: { required: !isEdit },
          options_data: userData,
        },
      ],
    },
  ];

  const redirect = getRegister(url);

  function onSubmit(payload: any, id: string | number) {
    const params = { ...payload };

    if (id) {
      return {
        id,
        api: `${API_SCRUTNIY_WORKFLOWS}`,
        params,
      };
    }

    return {
      api: `${API_SCRUTNIY_WORKFLOWS}`,
      params,
    };
  }

  return { sections, redirect, onSubmit };
};
